import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../../store";
import { PostData } from "../../../../services/FetchData";
import {
  approveOrRejectSynonyms,
  previewSynonymBeforeAfter,
  runSynonymGateCheck,
} from "../../../../constants/aiSynonymsApi";

const PLATFORMS = {
  MAGENTO: "magento",
  SHOPIFY: "shopify",
  BIGCOMMERCE: "bigcommerce",
  WOOCOMMERCE: "woocommerce",
  DRUPAL: "drupal",
};

const AISynonymPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWebsiteId, websiteData, defaultStoreView, setAddPageOpen } =
    useContext(ProductContext);
  const query = location.state?.query || "";
  const userFriendlyMessage = location.state?.userFriendlyMessage || "";
  const rawSynonyms = location.state?.synonyms || location.state?.aiTexts || [];
  const platformFromState = location.state?.platform || "";
  const storeViewFromState = location.state?.storeView || "";
  const synonymsId = location.state?.synonymsId || null;
  const aiTexts = Array.isArray(location.state?.aiTexts) ? location.state.aiTexts : [];
  const [isApproved, setIsApproved] = useState(!!location.state?.isApproved);

  const [loading, setLoading] = useState(true);
  const [productsBySynonym, setProductsBySynonym] = useState({});
  const [expandedSynonyms, setExpandedSynonyms] = useState({});
  const [approving, setApproving] = useState(false);
  const [rankingPreview, setRankingPreview] = useState(null);
  const [gateResult, setGateResult] = useState(null);
  const [rankingPreviewLoading, setRankingPreviewLoading] = useState(false);

  const normalizedSynonyms = useMemo(
    () =>
      (Array.isArray(rawSynonyms) ? rawSynonyms : [])
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter(Boolean),
    [rawSynonyms],
  );
  const platform = useMemo(() => {
    const contextPlatform = websiteData?.[currentWebsiteId]?.websiteType;
    return String(contextPlatform || platformFromState || "")
      .toLowerCase()
      .trim();
  }, [websiteData, currentWebsiteId, platformFromState]);

  const storeView = useMemo(() => {
    if (platform !== PLATFORMS.MAGENTO) return "";
    return String(storeViewFromState || defaultStoreView || "").trim();
  }, [platform, storeViewFromState, defaultStoreView]);

  useEffect(() => {
    setAddPageOpen(true);
    return () => {
      setAddPageOpen(false);
    };
  }, [setAddPageOpen]);

  useEffect(() => {
    const initialExpanded = normalizedSynonyms.reduce((acc, synonym) => {
      acc[synonym] = true;
      return acc;
    }, {});
    setExpandedSynonyms(initialExpanded);
  }, [normalizedSynonyms]);

  useEffect(() => {
    setIsApproved(!!location.state?.isApproved);
  }, [location.state?.isApproved]);

  useEffect(() => {
    if (!currentWebsiteId || normalizedSynonyms.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    PostData("ms1", "api/v1/aisynonyms/preview-products", {
      websiteId: currentWebsiteId,
      platform,
      storeView,
      searchPhrases: normalizedSynonyms,
      page: 1,
      perPage: 20,
    })
      .then((res) => {
        const byPhrase = Array.isArray(res?.data?.byPhrase) ? res.data.byPhrase : [];
        const results = byPhrase.map((item) => ({
          synonym: item?.searchPhrase || "",
          products: Array.isArray(item?.products) ? item.products : [],
        }));

        if (results.length === 0) {
          return normalizedSynonyms.map((synonym) => ({
            synonym,
            products: [],
          }));
        }
        return results;
      })
      .catch(() =>
        normalizedSynonyms.map((synonym) => ({
          synonym,
          products: [],
        })),
      )
      .then((results) => {
        if (cancelled) return;
        const grouped = results.reduce((acc, item) => {
          acc[item.synonym] = item.products;
          return acc;
        }, {});
        setProductsBySynonym(grouped);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentWebsiteId, normalizedSynonyms, platform, storeView]);

  const synonymListForGate = useMemo(() => {
    const fromAi = aiTexts.filter(Boolean);
    if (fromAi.length) return fromAi;
    return normalizedSynonyms;
  }, [aiTexts, normalizedSynonyms]);

  useEffect(() => {
    if (
      platform !== PLATFORMS.DRUPAL ||
      !currentWebsiteId ||
      !query ||
      synonymListForGate.length === 0
    ) {
      setRankingPreview(null);
      setGateResult(null);
      return;
    }

    let cancelled = false;
    setRankingPreviewLoading(true);

    Promise.all([
      previewSynonymBeforeAfter({
        websiteId: currentWebsiteId,
        searchTerm: query,
        synonyms: synonymListForGate,
      }),
      runSynonymGateCheck({
        websiteId: currentWebsiteId,
        searchTerm: query,
        synonyms: synonymListForGate,
      }),
    ])
      .then(([previewRes, gateRes]) => {
        if (cancelled) return;
        setRankingPreview(previewRes?.data ?? previewRes ?? null);
        setGateResult(gateRes?.data ?? gateRes ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setRankingPreview(null);
          setGateResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) setRankingPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [platform, currentWebsiteId, query, synonymListForGate]);

  const gateBlocked = gateResult?.ok === false || gateResult?.data?.ok === false;
  const gateOk = gateResult?.ok === true || gateResult?.data?.ok === true;

  const handleApprovalChange = async () => {
    if (!synonymsId) {
      toast.error("Synonym id not found.");
      return;
    }

    const action = isApproved ? "reject" : "approve";
    if (action === "approve" && platform === PLATFORMS.DRUPAL && gateBlocked) {
      toast.error("Publish blocked: protected regression gate failed.");
      return;
    }
    setApproving(true);

    try {
      const response = await approveOrRejectSynonyms({
        websiteId: currentWebsiteId,
        synonymsId,
        searchTerm: query,
        synonyms: aiTexts,
        action,
      });

      if (response?.status === "success") {
        setIsApproved(action === "approve");
        toast.success(
          action === "approve"
            ? "Synonym approved successfully."
            : "Synonym rejected successfully.",
        );
      } else {
        const gateMsg =
          response?.data?.gateBlocked || response?.data?.data?.gateBlocked
            ? "Publish blocked by protected regression gate."
            : null;
        toast.error(gateMsg || response?.message || "Failed to update approval.");
      }
    } catch (err) {
      console.error("Error updating synonym approval:", err);
      toast.error("Failed to update approval.");
    }

    setApproving(false);
  };

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">AI Suggested Synonym Product Preview</h4>
          <div className="text-muted small">
            Search Query: <strong>{query || "-"}</strong>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {synonymsId && (
            <button
              type="button"
              className={`btn btn-sm ${isApproved ? "btn-danger" : "btn-success"}`}
              onClick={handleApprovalChange}
              disabled={approving || (platform === PLATFORMS.DRUPAL && gateBlocked && !isApproved)}
            >
              {approving ? "Saving..." : isApproved ? "Reject" : "Approve"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>
      {userFriendlyMessage && (
        <div className="alert alert-info py-2">
          <div className="small mb-0">{userFriendlyMessage}</div>
        </div>
      )}

      {platform === PLATFORMS.DRUPAL && query && synonymListForGate.length > 0 && (
        <div className="card mb-3 border">
          <div className="card-body py-3">
            <h6 className="mb-2">Ranking Before / After (protected gate)</h6>
            {rankingPreviewLoading ? (
              <div className="d-flex align-items-center gap-2 small text-muted">
                <Spinner size="sm" />
                Running ranking preview and regression gate...
              </div>
            ) : (
              <>
                {gateOk && (
                  <div className="alert alert-success py-2 small mb-2 mb-0">
                    Protected regression gate: PASS — safe to approve.
                  </div>
                )}
                {gateBlocked && (
                  <div className="alert alert-danger py-2 small mb-2">
                    Protected regression gate: FAIL — approve is blocked.
                  </div>
                )}
                {rankingPreview?.diff && (
                  <div className="small">
                    <div className="mb-1">
                      <strong>Before:</strong>{" "}
                      {(rankingPreview.diff.beforeSkus || []).slice(0, 8).join(", ") || "—"}
                    </div>
                    <div className="mb-1">
                      <strong>After:</strong>{" "}
                      {(rankingPreview.diff.afterSkus || []).slice(0, 8).join(", ") || "—"}
                    </div>
                    {(rankingPreview.diff.added?.length > 0 ||
                      rankingPreview.diff.removed?.length > 0) && (
                      <div className="text-muted">
                        Added: {(rankingPreview.diff.added || []).join(", ") || "none"} · Removed:{" "}
                        {(rankingPreview.diff.removed || []).join(", ") || "none"}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner size="sm" />
          <span>Loading products for suggested synonyms...</span>
        </div>
      ) : normalizedSynonyms.length === 0 ? (
        <div className="alert alert-light border">No suggested synonyms found.</div>
      ) : (
        normalizedSynonyms.map((synonym) => {
          const products = productsBySynonym[synonym] || [];
          const isExpanded = expandedSynonyms[synonym];
          return (
            <div key={synonym} className="mb-4">
              <button
                type="button"
                className="btn btn-light border w-100 d-flex align-items-center justify-content-between mb-2"
                onClick={() =>
                  setExpandedSynonyms((prev) => ({
                    ...prev,
                    [synonym]: !prev[synonym],
                  }))
                }
              >
                <span>
                  Synonym: <span className="text-primary">{synonym}</span>
                </span>
                <span>{isExpanded ? "−" : "+"}</span>
              </button>
              {isExpanded &&
                (products.length === 0 ? (
                  <div className="text-muted small border rounded p-3 bg-light">
                    No products found for this synonym.
                  </div>
                ) : (
                  <div className="row g-3">
                    {products.map((product, index) => (
                      <div key={`${synonym}-${product?.id || index}`} className="col-12 col-md-6 col-lg-4 col-xl-3">
                        <div className="card h-100 shadow-sm border-0">
                          <div className="p-2 text-center" style={{ minHeight: "220px" }}>
                            <img
                              src={
                                product?.imageUrl ||
                                product?.image ||
                                "https://storage.googleapis.com/roundclicksview/No%20Product%20Image%20Available.png"
                              }
                              alt={product?.name || product?.title || "Product"}
                              className="img-fluid"
                              style={{ maxHeight: "200px", objectFit: "contain" }}
                            />
                          </div>
                          <div className="card-body pt-0">
                            <div className="fw-semibold mb-1" style={{ minHeight: "42px" }}>
                              {product?.name || product?.title || "-"}
                            </div>
                            <div className="small text-muted mb-2">SKU: {product?.sku || "-"}</div>
                            <div className="small">
                              Price:{" "}
                              <strong>
                                {typeof product?.price === "number" ? `$${product.price}` : "-"}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AISynonymPreview;
